import React from "react";
import moment from "moment";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export type WageDueTone = "ok" | "warning" | "overdue";

export interface WageDueStatus {
  daysLeft: number;
  label: string;
  tone: WageDueTone;
  nextDueDate: string; // YYYY-MM-DD
}


export const getWageDueStatus = (
  lastPaidDate: string | Date,
  cycleDays: number
): WageDueStatus => {
  const nextDue = moment(lastPaidDate).add(cycleDays, "days").startOf("day");
  const daysLeft = nextDue.diff(moment().startOf("day"), "days");
  const nextDueDate = nextDue.format("YYYY-MM-DD");

  if (daysLeft < 0) {
    const overdueBy = Math.abs(daysLeft);
    return {
      daysLeft,
      nextDueDate,
      label: overdueBy === 1 ? "Overdue by 1 day" : `Overdue by ${overdueBy} days`,
      tone: "overdue",
    };
  }
  if (daysLeft === 0) {
    return { daysLeft, nextDueDate, label: "Due today", tone: "warning" };
  }
  if (daysLeft === 1) {
    return { daysLeft, nextDueDate, label: "1 day left", tone: "warning" };
  }
  if (daysLeft <= 3) {
    return { daysLeft, nextDueDate, label: `${daysLeft} days left`, tone: "warning" };
  }
  return { daysLeft, nextDueDate, label: `${daysLeft} days left`, tone: "ok" };
};

const toneClasses: Record<WageDueTone, string> = {
  ok: "text-green-600 border-green-600 bg-green-50",
  warning: "text-amber-600 border-amber-600 bg-amber-50",
  overdue: "text-red-600 border-red-600 bg-red-50",
};

const toneIcon: Record<WageDueTone, React.ElementType> = {
  ok: CheckCircle2,
  warning: Clock,
  overdue: AlertTriangle,
};


export const WageDueBadge: React.FC<{ lastPaidDate: string; cycleDays: number }> = ({
  lastPaidDate,
  cycleDays,
}) => {
  const status = getWageDueStatus(lastPaidDate, cycleDays);
  const Icon = toneIcon[status.tone];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full whitespace-nowrap ${toneClasses[status.tone]}`}
    >
      <Icon size={12} />
      {status.label}
    </span>
  );
};

export const PAY_CYCLE_OPTIONS = [
  { label: "Every 10 days", value: 10 },
  { label: "Every 15 days", value: 15 },
  { label: "Every 30 days", value: 30 },
];